import {
  BathAccessoryCategory,
  BathAccessorySubCategory,
} from "./bathAccessoriesTypes";

export const bathAccessoryCategories: BathAccessoryCategory[] = [
  {
    id: 1,
    name: "Towel Bars & Rings",
    description:
      "Premium towel bars and rings in various sizes and finishes for organized and stylish towel storage.",
    keyCharacteristics: [
      "Corrosion resistant finish",
      "Multiple size options",
      "Easy wall mounting",
      "Modern design aesthetics",
    ],
    commonMaterials: [
      "Stainless steel 304 grade",
      "Brass with chrome plating",
      "Aluminum alloy construction",
      "Polymer end caps",
    ],
    primaryUse: [
      "Bathroom towel storage",
      "Hand towel hanging",
      "Guest bathroom accessories",
      "Hotel bathroom amenities",
      "Spa changing areas",
    ],
    image360: "/api/placeholder/800/600",
    images360: [
      "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&h=600&fit=crop", // Towel bar front view
      "https://images.unsplash.com/photo-1604709177225-055f99402ea3?w=800&h=600&fit=crop", // Towel ring close-up
      "https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?w=800&h=600&fit=crop", // Installation view
      "https://images.unsplash.com/photo-1507089947368-19c1da9775ae?w=800&h=600&fit=crop", // Chrome finish detail
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600&fit=crop", // Wall mounting system
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&h=600&fit=crop", // Modern design
      "https://images.unsplash.com/photo-1571736772567-3aa763ff559a?w=800&h=600&fit=crop", // Complete setup
      "https://images.unsplash.com/photo-1574269269401-859094eee3a5?w=800&h=600&fit=crop", // Bathroom integration
    ],
    thumbnails: [
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
    ],
    finishOptions: ["Chrome", "Brushed Steel", "Matte Black", "Gold"],
    warranty: "5 years against manufacturing defects",
    specifications: {
      material: "Stainless steel with corrosion-resistant coating",
      sizes: ["18-inch towel bar", "24-inch towel bar", "6-inch towel ring"],
      finish: ["Chrome", "Brushed Steel", "Matte Black", "Gold"],
      mounting: "Wall-mounted with concealed screws",
      maintenance: "Wipe clean with soft cloth, avoid abrasive cleaners",
    },
    useCases: [
      {
        location: "Master Bathroom",
        recommendation: "24-inch towel bar with matching towel ring set",
        keyChecks: [
          "Wall space availability",
          "Mounting surface suitability",
          "Coordination with other fixtures",
        ],
      },
      {
        location: "Guest Powder Room",
        recommendation: "Compact towel ring for space-efficient storage",
        keyChecks: [
          "Limited wall space utilization",
          "Easy guest accessibility",
          "Design coordination",
        ],
      },
      {
        location: "Family Bathroom",
        recommendation: "Multiple towel bars for family members",
        keyChecks: [
          "Multiple user requirements",
          "Height accessibility for children",
          "Durable construction for frequent use",
        ],
      },
      {
        location: "Luxury Hotel Suite",
        recommendation: "Premium finish towel accessories for guest comfort",
        keyChecks: [
          "Commercial durability requirements",
          "Luxury aesthetic appeal",
          "Easy maintenance for housekeeping",
        ],
      },
    ],
  },
  {
    id: 2,
    name: "Soap Dispensers & Holders",
    description:
      "Hygienic soap dispensers and traditional soap holders for convenient and stylish bathroom soap storage.",
    keyCharacteristics: [
      "Hygienic touch-free options",
      "Easy refill mechanisms",
      "Leak-proof construction",
      "Stylish design integration",
    ],
    commonMaterials: [
      "Stainless steel body",
      "Tempered glass containers",
      "Plastic pump mechanisms",
      "Rubber sealing gaskets",
    ],
    primaryUse: [
      "Hand washing stations",
      "Shower soap storage",
      "Kitchen sink areas",
      "Commercial restrooms",
      "Hotel bathroom amenities",
    ],
    image360: "/api/placeholder/800/600",
    images360: [
      "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&h=600&fit=crop", // Soap dispenser
      "https://images.unsplash.com/photo-1604709177225-055f99402ea3?w=800&h=600&fit=crop", // Wall-mounted holder
      "https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?w=800&h=600&fit=crop", // Pump mechanism
      "https://images.unsplash.com/photo-1507089947368-19c1da9775ae?w=800&h=600&fit=crop", // Touch-free sensor
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600&fit=crop", // Refill system
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&h=600&fit=crop", // Installation detail
      "https://images.unsplash.com/photo-1571736772567-3aa763ff559a?w=800&h=600&fit=crop", // Modern design
      "https://images.unsplash.com/photo-1574269269401-859094eee3a5?w=800&h=600&fit=crop", // Complete setup
    ],
    thumbnails: [
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
    ],
    finishOptions: ["Chrome", "Brushed Nickel", "Matte Black", "White"],
    warranty: "3 years on pump mechanism, 5 years on housing",
    specifications: {
      material: "Stainless steel with glass or plastic container",
      sizes: ["350ml capacity", "500ml capacity", "1000ml capacity"],
      finish: ["Chrome", "Brushed Nickel", "Matte Black", "White"],
      mounting: "Wall-mounted or countertop installation",
      maintenance: "Regular cleaning and refilling, pump lubrication",
    },
    useCases: [
      {
        location: "Modern Bathroom",
        recommendation: "Touch-free automatic dispenser for hygiene",
        keyChecks: [
          "Power source availability",
          "Sensor positioning",
          "Refill accessibility",
        ],
      },
      {
        location: "Family Kitchen",
        recommendation: "High-capacity dispenser for frequent use",
        keyChecks: [
          "Heavy usage durability",
          "Easy refill mechanism",
          "Child-friendly operation",
        ],
      },
      {
        location: "Commercial Restroom",
        recommendation: "Vandal-resistant dispenser with large capacity",
        keyChecks: [
          "Security mounting system",
          "Commercial-grade construction",
          "Easy maintenance access",
        ],
      },
      {
        location: "Luxury Spa",
        recommendation: "Premium finish dispenser matching décor",
        keyChecks: [
          "Design aesthetic integration",
          "Premium soap compatibility",
          "Quiet operation",
        ],
      },
    ],
  },
  {
    id: 3,
    name: "Bathroom Mirrors",
    description:
      "High-quality bathroom mirrors with LED lighting, anti-fog features, and elegant frames for perfect grooming.",
    keyCharacteristics: [
      "LED backlighting options",
      "Anti-fog heating systems",
      "Safety backing film",
      "Various frame designs",
    ],
    commonMaterials: [
      "Silver-backed glass",
      "Aluminum LED strips",
      "Stainless steel frames",
      "Polymer safety backing",
    ],
    primaryUse: [
      "Vanity mirror installation",
      "Grooming and shaving",
      "Makeup application",
      "Bathroom décor enhancement",
      "Space amplification",
    ],
    image360: "/api/placeholder/800/600",
    images360: [
      "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop", // LED mirror
      "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&h=600&fit=crop", // Anti-fog feature
      "https://images.unsplash.com/photo-1604709177225-055f99402ea3?w=800&h=600&fit=crop", // Frame detail
      "https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?w=800&h=600&fit=crop", // Installation view
      "https://images.unsplash.com/photo-1507089947368-19c1da9775ae?w=800&h=600&fit=crop", // Lighting system
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600&fit=crop", // Control panel
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&h=600&fit=crop", // Modern bathroom
      "https://images.unsplash.com/photo-1571736772567-3aa763ff559a?w=800&h=600&fit=crop", // Complete setup
    ],
    thumbnails: [
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
    ],
    finishOptions: ["Frameless", "Chrome Frame", "Black Frame", "Wood Frame"],
    warranty: "5 years on LED system, 3 years on anti-fog",
    specifications: {
      material: "Silver-backed mirror glass with safety film",
      sizes: ["24x32 inches", "30x40 inches", "36x48 inches"],
      finish: ["Frameless", "Chrome Frame", "Black Frame", "Wood Frame"],
      mounting: "Wall-mounted with hidden brackets",
      maintenance: "Glass cleaner, LED system self-maintaining",
    },
    useCases: [
      {
        location: "Master Bathroom Vanity",
        recommendation: "Large LED mirror with anti-fog for perfect grooming",
        keyChecks: [
          "Electrical connection for LED",
          "Anti-fog power requirements",
          "Wall mounting structure",
        ],
      },
      {
        location: "Guest Powder Room",
        recommendation: "Elegant framed mirror for décor enhancement",
        keyChecks: [
          "Space proportions",
          "Lighting coordination",
          "Frame style matching",
        ],
      },
      {
        location: "Spa Bathroom",
        recommendation: "Premium LED mirror with multiple lighting modes",
        keyChecks: [
          "Spa ambiance requirements",
          "Dimming control integration",
          "Professional installation",
        ],
      },
      {
        location: "Small Bathroom",
        recommendation: "Frameless mirror to maximize space perception",
        keyChecks: [
          "Space optimization",
          "Light reflection enhancement",
          "Safety considerations",
        ],
      },
    ],
  },
  {
    id: 4,
    name: "Toilet Paper Holders",
    description:
      "Functional and stylish toilet paper holders in various designs from basic to luxury with easy paper loading.",
    keyCharacteristics: [
      "Easy paper roll loading",
      "Secure roll retention",
      "Corrosion resistant finish",
      "Various mounting options",
    ],
    commonMaterials: [
      "Stainless steel construction",
      "Chrome plated brass",
      "Plastic end caps",
      "Spring-loaded mechanisms",
    ],
    primaryUse: [
      "Toilet area accessibility",
      "Bathroom convenience",
      "Commercial restroom use",
      "Guest bathroom essentials",
      "Accessibility compliance",
    ],
    image360: "/api/placeholder/800/600",
    images360: [
      "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&h=600&fit=crop", // TP holder design
      "https://images.unsplash.com/photo-1604709177225-055f99402ea3?w=800&h=600&fit=crop", // Wall mounting
      "https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?w=800&h=600&fit=crop", // Loading mechanism
      "https://images.unsplash.com/photo-1507089947368-19c1da9775ae?w=800&h=600&fit=crop", // Modern design
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600&fit=crop", // Installation detail
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&h=600&fit=crop", // Chrome finish
      "https://images.unsplash.com/photo-1571736772567-3aa763ff559a?w=800&h=600&fit=crop", // Bathroom setup
      "https://images.unsplash.com/photo-1574269269401-859094eee3a5?w=800&h=600&fit=crop", // Complete view
    ],
    thumbnails: [
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
    ],
    finishOptions: ["Chrome", "Brushed Nickel", "Matte Black", "Bronze"],
    warranty: "3 years against manufacturing defects",
    specifications: {
      material: "Stainless steel with spring mechanism",
      sizes: ["Standard single roll", "Double roll holder", "Recessed mount"],
      finish: ["Chrome", "Brushed Nickel", "Matte Black", "Bronze"],
      mounting: "Wall-mounted or recessed installation",
      maintenance: "Occasional cleaning and spring lubrication",
    },
    useCases: [
      {
        location: "Family Bathroom",
        recommendation: "Double roll holder for convenience and backup",
        keyChecks: [
          "Wall space for double width",
          "User accessibility height",
          "Easy loading mechanism",
        ],
      },
      {
        location: "Guest Powder Room",
        recommendation: "Elegant single holder matching other fixtures",
        keyChecks: [
          "Design coordination",
          "Guest-friendly operation",
          "Finish matching",
        ],
      },
      {
        location: "Commercial Restroom",
        recommendation: "Heavy-duty holder with vandal resistance",
        keyChecks: [
          "Commercial durability",
          "Security mounting",
          "Maintenance accessibility",
        ],
      },
      {
        location: "Accessible Bathroom",
        recommendation: "ADA-compliant height and easy-reach design",
        keyChecks: [
          "ADA height requirements",
          "Wheelchair accessibility",
          "Easy operation for limited mobility",
        ],
      },
    ],
  },
  {
    id: 5,
    name: "Robe Hooks & Hangers",
    description:
      "Stylish and functional robe hooks and hangers for bathroom organization and clothing storage.",
    keyCharacteristics: [
      "High weight capacity",
      "Smooth hook design",
      "Corrosion resistant coating",
      "Space-efficient mounting",
    ],
    commonMaterials: [
      "Solid brass construction",
      "Stainless steel hooks",
      "Chrome plated finish",
      "Zinc alloy components",
    ],
    primaryUse: [
      "Robe and towel hanging",
      "Clothing organization",
      "Bathroom storage solutions",
      "Spa changing areas",
      "Hotel room amenities",
    ],
    image360: "/api/placeholder/800/600",
    images360: [
      "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&h=600&fit=crop", // Robe hook design
      "https://images.unsplash.com/photo-1604709177225-055f99402ea3?w=800&h=600&fit=crop", // Multiple hooks
      "https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?w=800&h=600&fit=crop", // Wall mounting
      "https://images.unsplash.com/photo-1507089947368-19c1da9775ae?w=800&h=600&fit=crop", // Premium finish
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600&fit=crop", // Installation view
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&h=600&fit=crop", // Modern bathroom
      "https://images.unsplash.com/photo-1571736772567-3aa763ff559a?w=800&h=600&fit=crop", // Hook detail
      "https://images.unsplash.com/photo-1574269269401-859094eee3a5?w=800&h=600&fit=crop", // Complete setup
    ],
    thumbnails: [
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
    ],
    finishOptions: ["Chrome", "Brushed Gold", "Matte Black", "Copper"],
    warranty: "5 years against manufacturing defects",
    specifications: {
      material: "Solid brass with corrosion-resistant plating",
      sizes: ["Single hook", "Double hook", "Triple hook rail"],
      finish: ["Chrome", "Brushed Gold", "Matte Black", "Copper"],
      mounting: "Wall-mounted with concealed screws",
      maintenance: "Regular cleaning with soft cloth",
    },
    useCases: [
      {
        location: "Master Bathroom",
        recommendation: "Multiple hooks for robes and clothing organization",
        keyChecks: [
          "Wall space planning",
          "Height accessibility",
          "Weight capacity requirements",
        ],
      },
      {
        location: "Guest Bedroom Bathroom",
        recommendation: "Convenient hooks for guest clothing storage",
        keyChecks: [
          "Guest accessibility",
          "Space efficiency",
          "Design coordination",
        ],
      },
      {
        location: "Spa Changing Room",
        recommendation: "Premium finish hooks for luxury experience",
        keyChecks: [
          "Luxury aesthetic appeal",
          "High-end finish durability",
          "Professional installation",
        ],
      },
      {
        location: "Children's Bathroom",
        recommendation: "Lower height hooks for child accessibility",
        keyChecks: [
          "Child-appropriate height",
          "Safety considerations",
          "Easy reach and use",
        ],
      },
    ],
  },
  {
    id: 6,
    name: "Bathroom Storage Solutions",
    description:
      "Organized storage solutions including shelves, cabinets, and organizers for bathroom essentials.",
    keyCharacteristics: [
      "Moisture resistant materials",
      "Modular design options",
      "Easy installation systems",
      "Various capacity options",
    ],
    commonMaterials: [
      "Tempered glass shelves",
      "Stainless steel frames",
      "Moisture-resistant MDF",
      "Aluminum supports",
    ],
    primaryUse: [
      "Toiletry organization",
      "Towel storage",
      "Cleaning supply storage",
      "Decorative item display",
      "Space optimization",
    ],
    image360: "/api/placeholder/800/600",
    images360: [
      "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop", // Storage shelves
      "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&h=600&fit=crop", // Bathroom cabinet
      "https://images.unsplash.com/photo-1604709177225-055f99402ea3?w=800&h=600&fit=crop", // Organizer system
      "https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?w=800&h=600&fit=crop", // Wall storage
      "https://images.unsplash.com/photo-1507089947368-19c1da9775ae?w=800&h=600&fit=crop", // Modular design
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600&fit=crop", // Installation view
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&h=600&fit=crop", // Complete storage
      "https://images.unsplash.com/photo-1571736772567-3aa763ff559a?w=800&h=600&fit=crop", // Organized bathroom
    ],
    thumbnails: [
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
      "/api/placeholder/200/200",
    ],
    finishOptions: ["White", "Wood Grain", "Matte Black", "Chrome"],
    warranty: "3 years on hardware, 1 year on finishes",
    specifications: {
      material: "Moisture-resistant materials with metal hardware",
      sizes: ["Single shelf 24-inch", "Double shelf unit", "Corner storage"],
      finish: ["White", "Wood Grain", "Matte Black", "Chrome"],
      mounting: "Wall-mounted or freestanding options",
      maintenance: "Regular cleaning, moisture protection",
    },
    useCases: [
      {
        location: "Small Bathroom",
        recommendation: "Corner storage to maximize limited space",
        keyChecks: [
          "Space optimization needs",
          "Corner mounting capability",
          "Storage capacity requirements",
        ],
      },
      {
        location: "Family Bathroom",
        recommendation: "Multiple storage units for family organization",
        keyChecks: [
          "Multiple user storage needs",
          "Child-safe installation",
          "Easy access for all family members",
        ],
      },
      {
        location: "Master Bathroom",
        recommendation: "Elegant storage matching vanity design",
        keyChecks: [
          "Design aesthetic coordination",
          "Premium material requirements",
          "Luxury finish integration",
        ],
      },
      {
        location: "Guest Bathroom",
        recommendation: "Functional storage for guest convenience",
        keyChecks: [
          "Guest-friendly organization",
          "Essential storage capacity",
          "Easy maintenance access",
        ],
      },
    ],
  },
];

export const bathAccessorySubCategories: BathAccessorySubCategory[] = [
  {
    id: 1,
    categoryId: 1,
    name: "Chrome Towel Bar 24-inch",
    description: "Premium chrome finish towel bar for elegant storage",
    image: "/api/placeholder/300/200",
    features: [
      "24-inch length",
      "Chrome finish",
      "Concealed mounting",
      "5-year warranty",
    ],
    image360: "/api/placeholder/800/600",
    price: "₹1,500 - ₹3,500",
    availability: "In Stock",
  },
  {
    id: 2,
    categoryId: 2,
    name: "Automatic Soap Dispenser",
    description: "Touch-free soap dispenser with sensor technology",
    image: "/api/placeholder/300/200",
    features: [
      "Sensor activated",
      "500ml capacity",
      "Rechargeable battery",
      "Adjustable volume",
    ],
    image360: "/api/placeholder/800/600",
    price: "₹3,000 - ₹6,000",
    availability: "In Stock",
  },
  {
    id: 3,
    categoryId: 3,
    name: "LED Backlit Mirror",
    description: "Anti-fog LED mirror with touch controls",
    image: "/api/placeholder/300/200",
    features: [
      "LED backlighting",
      "Anti-fog heating",
      "Touch controls",
      "Energy efficient",
    ],
    image360: "/api/placeholder/800/600",
    price: "₹8,000 - ₹15,000",
    availability: "In Stock",
  },
  {
    id: 4,
    categoryId: 4,
    name: "Double Toilet Paper Holder",
    description: "Convenient double roll holder for continuous supply",
    image: "/api/placeholder/300/200",
    features: [
      "Double roll capacity",
      "Spring mechanism",
      "Wall mounted",
      "Chrome finish",
    ],
    image360: "/api/placeholder/800/600",
    price: "₹1,200 - ₹2,500",
    availability: "In Stock",
  },
];
